// Pillar page configurations.
// Each pillar is a hub-and-spoke SEO landing page that defines a topic,
// links to spoke articles on the blog, and CTAs to /ai-mock-interview.
//
// To add a new pillar: add an object here, then add a route in App.jsx
// (uses PillarScreen) and a SEO branch in SeoManager.jsx that calls
// getPillarBySlug.

const PILLARS = [
  {
    slug: 'interview-prep',
    title: 'Interview Preparation: The Complete Guide for 2026',
    metaTitle: 'Interview Preparation Guide 2026 — Practice & Get Hired | JobSkills',
    metaDescription:
      'Complete guide to preparing for job interviews in 2026. Behavioral questions, STAR method, 7-day prep plan, mental game, and AI-powered practice.',
    badge: 'Hub Guide · Updated April 2026',
    h1: 'Interview Preparation: The Complete Guide for 2026',
    intro:
      "Most candidates don't fail interviews because they lack qualifications. They fail because they didn't practice the right things — or didn't practice at all. This guide breaks interview preparation into the four parts that actually move the needle, with deep-dives into the questions, frameworks, and mindsets that get offers.",
    definitionBlock: {
      question: 'What is interview preparation?',
      answer:
        "Interview preparation is the structured practice of researching the role, building a story bank from your resume, rehearsing answers to behavioral and technical questions, and simulating the pressure of a real interview through mock sessions. 70% of hiring managers cite poor preparation as the top reason qualified candidates don't get offers (PrepAway, 2025), so the goal isn't to read more — it's to practice out loud with feedback.",
    },
    keyTakeaways: [
      '70% of hiring managers say poor preparation is the #1 interview failure (PrepAway, 2025)',
      'Behavioral questions are 55% effective at predicting performance vs 10% for generic questions (DDI)',
      '92% of adults experience interview anxiety — and it measurably lowers performance scores',
      'Active practice (out loud, with feedback) outperforms passive review by 3–5×',
      'Seven focused days of structured prep beats three weeks of unfocused reading',
    ],
    sections: [
      {
        h2: 'How long should you prepare for a job interview?',
        body: [
          "If your interview is in seven days or less, you don't need three weeks. You need a deliberate plan. Most candidates either over-prepare (spending 20+ hours reading articles that don't change anything) or under-prepare (skim the company website the night before).",
          'A focused seven-day plan covers everything that matters: company research on Day 1, behavioral story bank on Days 2–3, mock interviews on Days 4–5, refinement and edge-case practice on Days 6–7. The structure forces you to spend time on the things that actually drive offer rates — practice and feedback — instead of passive reading.',
        ],
        spoke: {
          slug: '7-day-interview-prep-plan',
          title: 'The 7-Day Interview Prep Plan That Actually Works',
          description: 'Day-by-day playbook that turns a single week into measurable interview improvement.',
        },
      },
      {
        h2: 'What are behavioral interview questions and how do you answer them?',
        body: [
          'Behavioral questions follow the "Tell me about a time when…" format. Approximately 73% of employers use them as the backbone of their interview process (Carv, 2024). They\'re asked because past behavior is the best available predictor of future behavior — and unlike trivia, they\'re hard to fake.',
          'The five questions that trip up the most qualified candidates: tell me about a failure, describe a difficult colleague, a time you led without authority, a time you disagreed with your manager, and a time you missed a deadline. Each tests a different competency, and each has a structure that turns "blank stare" answers into 5/5 ones.',
        ],
        spoke: {
          slug: 'behavioral-interview-questions-how-to-answer',
          title: '5 Behavioral Interview Questions Most Candidates Get Wrong',
          description: 'The five questions hiring managers use to separate good candidates from great ones — and how to nail each.',
        },
      },
      {
        h2: 'How do you actually use the STAR method?',
        body: [
          "Everyone tells you to use STAR (Situation, Task, Action, Result). Almost no guide explains where people fail with it. The Situation and Task sections balloon to 60–90 seconds of background. The Action section uses \"we\" instead of \"I\". The Result has no number, no comparison, no measurable outcome.",
          'STAR done right is roughly 15% Situation, 10% Task, 50% Action, 25% Result. The Action section is what interviewers actually score — your specific decisions and reasoning. The Result anchors the whole answer in something measurable. Get those two right and your behavioral answers stop being stories and start being evidence.',
        ],
        spoke: {
          slug: 'star-method-interview-guide',
          title: 'The STAR Method: A Complete Guide to Any Behavioral Question',
          description: 'Real STAR-method examples and the structural mistakes that ruin most behavioral answers.',
        },
      },
      {
        h2: 'How do you answer "tell me about yourself"?',
        body: [
          "It's the most common opening question and the most commonly fumbled. Candidates either recite their resume from start to finish, ramble for three minutes, or freeze trying to figure out where to start.",
          "The answer is structurally simple: 60–90 seconds, three beats — present role and what you're great at, the relevant arc that brought you here, and why this specific role at this specific company is the next move. Make those three beats land and you set the tone for the rest of the interview.",
        ],
        spoke: {
          slug: 'tell-me-about-yourself-interview-answer',
          title: 'How to Answer "Tell Me About Yourself" in a Job Interview',
          description: 'The 60–90 second framework that makes the most asked interview question work for you.',
        },
      },
      {
        h2: 'Why do qualified candidates keep failing interviews?',
        body: [
          "It looks like a mystery, but it isn't. Interviews don't primarily test your qualifications — recruiters screened those before the call. Interviews test one specific skill: can you communicate your experience clearly under mild social pressure? That's a separate skill, and most candidates don't practice it.",
          "The fix isn't more research about the company. It's structured, repeated practice with actual feedback that names the specific structural problem in your answers — not vibes-based advice like \"be more confident.\" Mock interviews with real feedback are how you build the fluency that passive prep cannot give you.",
        ],
        spoke: {
          slug: 'why-qualified-candidates-fail-interviews',
          title: "Why Qualified Candidates Keep Failing Interviews (It's Not What You Think)",
          description: 'Interview rejection is more often a communication gap than a qualification gap. Here\'s the fix.',
        },
      },
    ],
    cta: {
      headline: 'Practice your next interview with AI feedback — 3 free sessions',
      sub: "Paste your resume and a job description. JobSkills generates personalized questions and gives BARS-scored feedback after every answer. No credit card needed.",
      action: 'Start free mock interview',
      href: '/ai-mock-interview',
    },
    faq: [
      {
        q: 'How many days do you need to prepare for an interview?',
        a: 'A focused 7-day plan is enough for most roles. The plan covers research, story-building, and 4–5 mock sessions with feedback. Less than 3 days requires triage: prioritize behavioral story bank and at least one full mock interview. More than 14 days is usually wasted on passive reading without enough active practice.',
      },
      {
        q: 'What is the most important part of interview preparation?',
        a: 'Active practice with feedback. Reading articles, watching videos, and reviewing question lists are useful inputs but produce minimal performance gain. The candidates who improve fastest practice answering questions out loud and get specific feedback on what fell flat — usually the Action section of their STAR answers.',
      },
      {
        q: 'Are behavioral interview questions really that important?',
        a: 'Yes. Approximately 73% of employers use behavioral questions, and they correlate ~5× more with on-the-job performance than generic interview questions. If you can only prepare for one type of question, prepare for behavioral.',
      },
      {
        q: 'Can AI replace working with a human interview coach?',
        a: 'For practice volume and consistency, yes — AI mock interviews are 24/7, infinitely patient, and 10–100× cheaper. For high-stakes negotiation coaching or executive presence work, a human coach still adds value. Most candidates benefit from AI for daily practice and a human coach for one or two sessions before the final round.',
      },
      {
        q: 'How do I deal with interview anxiety?',
        a: 'Interview anxiety is normal — 92% of adults report it. The fix is twofold: technique (practice out loud often enough that the format feels familiar; box breathing before the interview to lower physiological arousal) and reframe (anxiety symptoms and excitement symptoms are physiologically identical — labeling the feeling as excitement measurably improves performance).',
      },
    ],
  },
  {
    slug: 'interview-anxiety',
    title: 'Interview Anxiety: What Actually Helps in 2026',
    metaTitle: 'Interview Anxiety — What Actually Helps | JobSkills',
    metaDescription:
      'Interview anxiety is normal — 92% of adults experience it. Learn the cognitive, physiological, and practice-based techniques that actually reduce it.',
    badge: 'Mindset Guide · Updated April 2026',
    h1: 'Interview Anxiety: What Actually Helps',
    intro:
      "Interview anxiety isn't a character flaw — it's a near-universal physiological response to high-stakes social evaluation. The question isn't how to eliminate it (you can't) but how to manage it so it stops costing you offers.",
    definitionBlock: {
      question: 'What causes interview anxiety?',
      answer:
        "Interview anxiety is the physiological stress response triggered by social evaluation under uncertainty. 92% of adults report it, and it measurably lowers performance scores by reducing working memory and disrupting structured speech. The two evidence-based interventions are repeated low-stakes practice (which makes the situation feel familiar) and cognitive reappraisal — relabeling anxiety symptoms as excitement, which improves performance without reducing the underlying physiology.",
    },
    keyTakeaways: [
      '92% of adults experience interview anxiety — and it measurably lowers performance',
      'Anxiety and excitement share identical physiology — only the label differs',
      'Cognitive reappraisal ("I\'m excited") outperforms suppression ("calm down") in trials',
      'Mock interview practice cuts subjective anxiety by 40–60% after 5–7 sessions',
      'Box breathing (4-4-4-4) lowers heart rate within 90 seconds and is undetectable on camera',
    ],
    sections: [
      {
        h2: 'Why does my anxiety spike right before the interview?',
        body: [
          "Your body interprets a high-stakes interview the same way it interprets physical danger: cortisol up, heart rate up, working memory down. That's not weakness — it's evolution. The problem is that you need working memory to deliver structured STAR answers, and panic kills it.",
          'The intervention that consistently works in trials is cognitive reappraisal: telling yourself "I\'m excited" out loud before the interview. The physiology of anxiety and excitement is identical — what differs is the label your brain attaches to it. Reappraisal turns the spike into useful arousal instead of a performance killer.',
        ],
        spoke: {
          slug: 'interview-anxiety',
          title: "Interview Anxiety Is Normal. Here's What Actually Helps.",
          description: 'The science-backed techniques that reduce interview anxiety without pretending it doesn\'t exist.',
        },
      },
      {
        h2: 'How do you win the mental game of an interview?',
        body: [
          "Confidence isn't a feeling — it's a result of preparation. The interviewers who dominate aren't the ones who feel less anxiety; they're the ones who've practiced enough that the format feels familiar even when the questions don't.",
          'The mental game has three pillars: structured pre-interview routine (sleep, nutrition, breath work), an in-interview reset (a phrase or gesture that grounds you when a question lands wrong), and a post-interview practice that prevents one bad question from cascading into the rest of the conversation.',
        ],
        spoke: {
          slug: 'mental-game-of-job-interviews',
          title: 'The Mental Game of Job Interviews (And How to Win It)',
          description: 'The pre-interview, in-interview, and post-question routines that separate composed candidates from anxious ones.',
        },
      },
    ],
    cta: {
      headline: 'Practice in low-stakes mode until interviews feel routine',
      sub: 'JobSkills lets you practice as many mock interviews as you want until your structured answers feel automatic. The format becomes familiar — and the anxiety stops eating your working memory.',
      action: 'Practice for free',
      href: '/ai-mock-interview',
    },
    faq: [
      {
        q: 'Is interview anxiety normal?',
        a: 'Yes — 92% of adults experience it. Mild anxiety improves performance by sharpening focus. Severe anxiety degrades performance by consuming working memory and disrupting structured speech. The goal is to keep it in the productive range, not eliminate it.',
      },
      {
        q: 'What is cognitive reappraisal for interviews?',
        a: 'Cognitive reappraisal is relabeling your physiological state. The body sensations of anxiety and excitement are identical — same heart rate, same breathing, same cortisol. Saying "I\'m excited" out loud before the interview tells your brain to interpret the arousal as performance fuel rather than threat response. Studies show this consistently outperforms trying to "calm down".',
      },
      {
        q: 'Does breathing actually help with interview anxiety?',
        a: 'Yes. Box breathing (inhale 4, hold 4, exhale 4, hold 4) for 90 seconds activates the parasympathetic nervous system and measurably lowers heart rate and cortisol. It works because slow exhales trigger the vagus nerve. It\'s undetectable on a video call and can be done while waiting for the interviewer to join.',
      },
      {
        q: 'How many mock interviews do I need to reduce anxiety?',
        a: '5–7 sessions typically cuts subjective anxiety by 40–60%. The mechanism is desensitization — the format becomes familiar enough that your nervous system stops treating it as a novel threat. Spread the sessions across 1–2 weeks for the best effect; cramming all of them in one day produces diminishing returns.',
      },
    ],
  },
  {
    slug: 'ai-interview-prep',
    title: 'AI Interview Prep: How It Works and Whether You Need It',
    metaTitle: 'AI Interview Prep — Does It Actually Work? | JobSkills',
    metaDescription:
      'AI interview preparation in 2026: how it works, where it beats human coaches, where it falls short, and the AI tools job seekers actually use.',
    badge: 'AI Tools Guide · Updated April 2026',
    h1: 'AI Interview Prep: How It Works and Whether You Need It',
    intro:
      "AI mock interview tools went from novelty to mainstream in under two years. 42% of candidates now use AI to prep, and the data on outcomes is finally clear enough to make an honest comparison. This guide explains how AI prep works, where it beats traditional methods, and where you still need a human in the loop.",
    definitionBlock: {
      question: 'What is AI interview preparation?',
      answer:
        "AI interview preparation uses large language models to generate personalized interview questions from your resume and a job description, simulate the back-and-forth of a real interview, and score your answers using a structured rubric like BARS (Behaviorally Anchored Rating Scale). Candidates using AI-assisted mock interviews show a 53% interview success rate compared to 29% for traditional prep (Shortlistd.io, 2025).",
    },
    keyTakeaways: [
      '42% of candidates use AI to prep — up from 8% in 2023',
      'AI mock interviews show 53% success rate vs 29% for traditional prep (Shortlistd.io, 2025)',
      'AI beats human coaches on volume, consistency, and cost — and matches them on feedback specificity',
      'Human coaches still win for high-stakes negotiation and executive presence work',
      'The best results come from AI for daily practice + 1–2 human sessions before final rounds',
    ],
    sections: [
      {
        h2: 'How does an AI mock interview actually work?',
        body: [
          "The good ones do four things: read your resume and the job description together, generate questions that target the gap between them, score your answers against a real rubric (BARS — what FAANG and consulting recruiters actually use), and track scores across sessions so you can see improvement.",
          "The mediocre ones throw generic questions at you and tell you your answer was \"good\" or \"could be better.\" That's not feedback — it's a chatbot. The differentiator is whether the AI can name the specific structural problem (\"your Action section described what happened but not why you decided\") rather than vibes.",
        ],
        spoke: {
          slug: 'ai-vs-traditional-interview-prep-2026',
          title: 'AI vs. Traditional Interview Prep: An Honest Comparison for 2026',
          description: 'Where AI mock interviews beat traditional prep, where they fall short, and what the success-rate data actually says.',
        },
      },
      {
        h2: 'AI mock interview vs. career coach: which prepares you better?',
        body: [
          "Different tools, different jobs. An AI mock interview gives you 50 sessions a month for the price of 30 minutes with a coach. A human coach gives you nuanced reads on tone, body language, and the soft signals that don't show up in transcripts.",
          'For practice volume — the thing that drives real fluency — AI wins decisively. For one-off high-stakes preparation (final round at a top company, salary negotiation), a human coach is still worth it. Most candidates get the best ROI by using AI for daily practice and booking a coach for the last 1–2 sessions before a critical interview.',
        ],
        spoke: {
          slug: 'ai-mock-interview-vs-career-coach',
          title: 'AI Mock Interview vs. Career Coach: Which Actually Prepares You Better?',
          description: 'A direct comparison across cost, feedback quality, scheduling, and outcomes — with honest verdicts on each axis.',
        },
      },
      {
        h2: 'Which AI tools actually help with job search?',
        body: [
          "Beyond mock interviews, AI now handles resume tailoring, application tracking, salary research, and outreach. The five tools that earn their place in a 2026 job search stack: an AI resume tailor, an AI mock interview platform, an AI application tracker, an AI salary research tool, and an AI outreach drafter.",
          'The trap to avoid: stacking too many AI tools without a workflow. Five well-used tools beat fifteen half-used ones. Pick one in each category, learn it deeply, and you\'ll move faster than someone juggling double the stack.',
        ],
        spoke: {
          slug: 'ai-tools-job-seekers-2026',
          title: '5 AI Tools That Actually Help With Your Job Search in 2026',
          description: 'A curated stack — not a hype list — of AI tools that measurably improve job search outcomes.',
        },
      },
      {
        h2: 'Can you prep for a FAANG interview without a paid coach?',
        body: [
          "Yes — and most successful FAANG candidates do. The barrier isn't access to a coach; it's the discipline to do enough structured practice. FAANG interviews follow specific rubrics (Amazon Leadership Principles, Google's structured behavioral, Meta's competency framework), and AI mock interviews can simulate all of them.",
          "The combination that consistently lands offers: 4–6 weeks of structured practice using AI for question generation and feedback, two or three sessions with a peer at the same level for soft-signal feedback, and a clear story bank tied to the company's leadership principles. No $200/hour coach required.",
        ],
        spoke: {
          slug: 'faang-interview-prep-without-coach',
          title: 'How to Prepare for a FAANG Interview Without Paying $200/hr for a Coach',
          description: 'The structured 4–6 week plan that lands FAANG offers without a paid coach — used by hundreds of candidates.',
        },
      },
    ],
    cta: {
      headline: 'Try the AI mock interview that reads your resume and the job description',
      sub: "Paste any job posting plus your resume. JobSkills generates personalized questions and BARS-scored feedback. 3 free sessions, no credit card.",
      action: 'Start free mock interview',
      href: '/ai-mock-interview',
    },
    faq: [
      {
        q: 'Does AI interview prep actually work?',
        a: 'The data says yes. Candidates using AI-assisted mock interviews report a 53% interview success rate compared to 29% for traditional prep (Shortlistd.io, 2025). The mechanism is simple: AI gives you the practice volume that traditional prep doesn\'t, with structured feedback that human friends and family typically don\'t provide.',
      },
      {
        q: 'Is AI interview prep better than ChatGPT?',
        a: 'For interview-specific prep, yes. ChatGPT can answer one question at a time but doesn\'t track your progress, doesn\'t use a structured scoring rubric, and doesn\'t simulate the back-and-forth of a real interview. Purpose-built AI mock interview tools are designed for the workflow — ChatGPT is a general assistant.',
      },
      {
        q: 'How is BARS scoring used in AI interviews?',
        a: 'BARS (Behaviorally Anchored Rating Scale) is the rubric real recruiters use at FAANG, consulting, and most mature companies. Each answer is scored 1–5 on specific competencies (communication, problem-solving, leadership, cultural fit) with concrete behavioral anchors at each level. AI mock interviews using BARS give you the same kind of feedback your real interviewer will be writing about you.',
      },
      {
        q: 'Should I still hire a human interview coach?',
        a: 'For most candidates: only for the final 1–2 sessions before a high-stakes interview. AI handles the volume practice, structure, and BARS feedback at 1–10% of the cost. A human coach adds value on tone, body language, and negotiation — the things that are hard to evaluate from a transcript.',
      },
      {
        q: 'What\'s the best AI mock interview tool for 2026?',
        a: 'The best tool is the one that reads your specific resume and the specific job description, gives BARS-scored feedback, tracks progress across sessions, and is cheap enough that you actually use it daily. JobSkills was built around exactly that workflow — paste resume + JD, get personalized questions, get instant BARS feedback. The free tier (3 sessions, no card) is enough to evaluate whether it works for your prep style.',
      },
    ],
  },
];

export function getPillarBySlug(slug) {
  return PILLARS.find((p) => p.slug === slug) || null;
}

export function getAllPillars() {
  return PILLARS;
}

export default PILLARS;
